// @ts-check
import { useMemo } from "react"
import { Node, mergeAttributes } from "@tiptap/core"
import { ReactNodeViewRenderer, NodeViewWrapper } from "@tiptap/react"
import { motion, useReducedMotion } from "framer-motion"
import { Check, X } from "lucide-react"
import { ChatMarkdown } from "../chat-bubble/ChatBubble"
import { DIFF_MARKER_CLOSE, DIFF_MARKER_OPEN } from "./diffMarkers"
import { wordDiff } from "./wordDiff"

/**
 * markdown-it plugin recognizing the @{...}@ diff marker as an atomic inline token.
 * @param {import("markdown-it")} md
 */
export function diffHunkMarkdownItPlugin(md) {
  md.inline.ruler.before("text", "diff_hunk", (/** @type {any} */ state, /** @type {boolean} */ silent) => {
    if (state.src.slice(state.pos, state.pos + DIFF_MARKER_OPEN.length) !== DIFF_MARKER_OPEN) return false
    const closeIdx = state.src.indexOf(DIFF_MARKER_CLOSE, state.pos + DIFF_MARKER_OPEN.length)
    if (closeIdx === -1) return false

    if (!silent) {
      const payload = state.src.slice(state.pos + DIFF_MARKER_OPEN.length, closeIdx)
      const token = state.push("html_inline", "", 0)
      token.content = `<span data-diff-hunk-payload="${payload}"></span>`
    }
    state.pos = closeIdx + DIFF_MARKER_CLOSE.length
    return true
  })
}

/**
 * The removed and the added text, with only the changed words highlighted.
 * @param {{ old_string?: string, new_string?: string }} props
 */
function DiffBody({ old_string, new_string }) {
  const parts = useMemo(() => wordDiff(old_string ?? "", new_string ?? ""), [old_string, new_string])

  /** @param {"removed" | "added"} side */
  function renderSide(side) {
    const other = side === "removed" ? "added" : "removed"
    return parts.filter(part => part.type !== other).map((part, index) => (
      <span
        key={index}
        className={part.type === side
          ? (side === "removed" ? "rounded-sm bg-red-200/90 dark:bg-red-500/30" : "rounded-sm bg-green-200/90 dark:bg-green-500/30")
          : undefined}
      >
        {part.text}
      </span>
    ))
  }

  return (
    <span className="block max-h-64 overflow-auto text-[13px] leading-6">
      {old_string && (
        <span className="flex bg-red-50 dark:bg-red-950/30">
          <span aria-hidden="true" className="select-none w-6 shrink-0 text-center text-red-600 dark:text-red-400/70">−</span>
          <span className="flex-1 min-w-0 whitespace-pre-wrap break-words py-0.5 pr-2.5 text-red-950 dark:text-red-200">
            <span className="sr-only">Removed: </span>
            {renderSide("removed")}
          </span>
        </span>
      )}
      {new_string && (
        <span className="flex bg-green-50 dark:bg-green-950/30">
          <span aria-hidden="true" className="select-none w-6 shrink-0 text-center text-green-700 dark:text-green-400/70">+</span>
          <span className="flex-1 min-w-0 whitespace-pre-wrap break-words py-0.5 pr-2.5 text-green-950 dark:text-green-200">
            <span className="sr-only">Added: </span>
            {renderSide("added")}
          </span>
        </span>
      )}
    </span>
  )
}

function DiffNodeView({ node, editor }) {
  const { id, old_string, new_string, rationale, status } = node.attrs
  const reduceMotion = useReducedMotion()

  const accept = (e) => { e.stopPropagation(); editor.extensionStorage.diffHunk?.onAccept?.(id) }
  const reject = (e) => { e.stopPropagation(); editor.extensionStorage.diffHunk?.onReject?.(id) }

  if (status === "stale") {
    return (
      <NodeViewWrapper as="span" data-diff-id={id} contentEditable={false} className="inline-flex items-baseline gap-1 mx-0.5 align-baseline">
        <span className="text-xs bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-400 border border-dashed border-yellow-400/60 rounded px-1.5 py-0.5">
          <span className="line-through opacity-60">{old_string}</span>
          <button onClick={reject} data-diff-action className="ml-1.5 hover:underline">dismiss</button>
        </span>
      </NodeViewWrapper>
    )
  }

  const isDrafting = status === "drafting"
  // Only the edit playback just added animates in; every setContent rebuilds all the cards.
  const animateIn = !reduceMotion && editor.extensionStorage.diffHunk?.animateId === id

  return (
    <NodeViewWrapper as="span" data-diff-id={id} contentEditable={false} className="inline-block w-full max-w-full sm:max-w-prose my-2 align-baseline">
      <motion.span
        initial={animateIn ? { opacity: 0, y: 10, scale: 0.985 } : false}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
        className="block rounded-lg border border-border overflow-hidden bg-background shadow-sm data-[drafting=true]:border-[#34d094]/60"
        data-drafting={isDrafting}
      >
        {/* Header strip — label + accept/reject */}
        <span className="flex items-center justify-between gap-2 px-2.5 py-1.5 bg-muted/50 border-b border-border">
          <span className="text-[11px] font-medium text-muted-foreground">
            {isDrafting ? "Adding suggestion…" : "Suggested edit"}
          </span>
          {!isDrafting && (
            <span data-diff-action className="flex items-center gap-1 shrink-0">
              <button
                type="button"
                onClick={reject}
                className="h-6 px-2 inline-flex items-center gap-1 rounded-md border border-border bg-transparent text-foreground/80 hover:text-destructive hover:border-destructive/40 hover:bg-destructive/10 text-[11px] font-medium transition-colors"
              >
                <X size={12} /> Reject
              </button>
              <button
                type="button"
                onClick={accept}
                className="h-6 px-2 inline-flex items-center gap-1 rounded-md bg-green-600 hover:bg-green-700 text-white text-[11px] font-medium transition-colors"
              >
                <Check size={12} /> Accept
              </button>
            </span>
          )}
        </span>

        {/* Rationale — rendered as full markdown (same as a normal agent chat
            response), not truncated to a single line, so multi-sentence or
            formatted reviewer notes are fully readable in place. */}
        {rationale && (
          <span className="block px-2.5 py-2 border-b border-border bg-muted/20 text-foreground prose prose-xs dark:prose-invert max-w-none [&>*:first-child]:mt-0 [&>*:last-child]:mb-0 [&_li]:text-[13px] [&_li]:leading-5 [&_p]:text-[13px] [&_p]:leading-5">
            <ChatMarkdown>{rationale}</ChatMarkdown>
          </span>
        )}

        <DiffBody old_string={old_string} new_string={new_string} />
      </motion.span>
    </NodeViewWrapper>
  )
}

export const DiffHunkExtension = Node.create({
  name: "diffHunk",
  group: "inline",
  inline: true,
  atom: true,

  addStorage() {
    return {
      /** @type {((id: string) => void) | null} */
      onAccept: null,
      /** @type {((id: string) => void) | null} */
      onReject: null,
      /** Id of the card that should animate in on the next render. @type {string | null} */
      animateId: null,
      // tiptap-markdown serializer: output old_string so getMarkdown() stays clean
      markdown: {
        serialize(state, node) {
          state.write(node.attrs.old_string || "")
        },
        parse: {
          setup(/** @type {any} */ markdownit) {
            markdownit.use(diffHunkMarkdownItPlugin)
          },
        },
      },
    }
  },

  addAttributes() {
    return {
      id: { default: null },
      old_string: { default: "" },
      new_string: { default: "" },
      rationale: { default: "" },
      status: { default: "pending" },
    }
  },

  parseHTML() {
    return [
      { tag: "span[data-diff-hunk]" },
      {
        tag: "span[data-diff-hunk-payload]",
        getAttrs(node) {
          try {
            const raw = /** @type {HTMLElement} */(node).getAttribute("data-diff-hunk-payload")
            if (!raw) return false
            const payload = JSON.parse(decodeURIComponent(raw))
            return {
              id: payload.id,
              old_string: payload.old_string,
              new_string: payload.new_string,
              rationale: payload.rationale,
              status: payload.status,
            }
          } catch {
            return false
          }
        },
      },
    ]
  },

  renderHTML({ node, HTMLAttributes }) {
    return ["span", mergeAttributes({ "data-diff-hunk": node.attrs.id }, HTMLAttributes)]
  },

  addNodeView() {
    return ReactNodeViewRenderer(DiffNodeView)
  },
})
